import { Component, OnInit } from '@angular/core';
import { flatten, DataGridTemplate } from '../../../../../shared/data-grid/data-grid.template';
import { BaseService } from '../../../../mesc/mesc.service';
import { GridDataResult } from '@progress/kendo-angular-grid';
import { FileInfo, FileState } from '@progress/kendo-angular-upload';
import { S3Service } from '../../../../../shared/services/s3.service';
import { HttpParams } from '@angular/common/http';
import { FilterableService } from '../../../../mesc/filterable-service';
import { RequestStatus } from '../../../workflow.service';
import { utilities } from '../../../../../../modules/utility';

@Component({
    selector: 'app-request-management',
    templateUrl: './request-management.component.html',
    styleUrls: ['./request-management.component.scss']
})
export class RequestManagementComponent extends DataGridTemplate implements OnInit {
    editForm;
    modifiedByService: FilterableService = new FilterableService();
    originatorNameService: FilterableService = new FilterableService();
    processNameService: FilterableService = new FilterableService();
    requestNumberService: FilterableService = new FilterableService();
    requestStatusService: FilterableService = new FilterableService();

    constructor(
        private readonly service: BaseService,
        private readonly s3Service: S3Service,
    ) {
        super();
        this.controls.edit = false;
        this.controls.add = false;
        this.controls.excel = false;
        this.controls.configure = true;
        this.sortable = true;
        this.isServerSideFiltering = true;
        this.filterable = 'menu';
        this.gridState.sort = [{ dir: 'asc', field: 'RequestNumber' }];
        this.controls.customToolBarButtons = this.customToolBarButtons;

    }

    ngOnInit() { }


    read(opts: any): Promise<any> {
        const filters = this.getFilters();
        const options = this.getSearchParams();

        return this.service.search(filters, 'ctwr/Request/Search', true, options)
            .then(x => {
                this.setLookupData(x.ReportLookupData);
                x.Requests = x.Requests.map(r => {
                    r.ModifiedDate = utilities.getLocalDateTime(r.ModifiedDate);
                    r.SubmissionDate = utilities.getLocalDateTime(r.SubmissionDate);
                    return r;
                });
                return {
                    data: x.Requests,
                    total: x.RecordCount
                } as GridDataResult
            });
    }


    /**
     * 
     * @param data 
     */
    private setLookupData(data) {
        if (!data) return;
        this.modifiedByService.next(data.ModifiedBy);
        this.originatorNameService.next(data.OriginatorName);
        this.processNameService.next(data.ProcessName);
        this.requestNumberService.next(data.RequestNumber);
        this.requestStatusService.next(data.RequestStatus);
    }

    create(parentEditDataItem: any): void { }
    edit(dataItem: any): void { }
    save(dataItem: any, isNew: boolean): Promise<any> { return null; }


    delete(dataItem: any): Promise<any> {
        return this.service.read(`ctwr/Request/Detail?requestId=${dataItem.RequestId}`)
            .then(x => {
                x.RequestAttachmentDtos = this.mapRequestAttachmentDto(x.RequestAttachmentDtos);
                if (x.RequestAttachmentDtos) {
                    this.deleteAllAttachments(x);
                }
                const _path = `ctwr/Request/DeleteAdminRequest?id=${dataItem.RequestId}`;
                return this.service.remove(_path, false);
            });
    }


    /**
     * 
     */
    isDisabled = (id: string, dataItem: any): boolean => {
        if (id && id.toLowerCase() === 'delete') {
            return dataItem.RequestStatus === RequestStatus.COMPLETE || dataItem.RequestStatus === RequestStatus.CANCELLED;
        } else {
            return false;
        }
    }


    /**
     * 
     * @param files 
     */
    private mapRequestAttachmentDto(files: any[]): FileInfo[] {
        return files ? files
            .map(i => {
                return {
                    name: i.FileName,
                    CreatedBy: i.CreatedBy,
                    CreatedDate: i.CreatedDate,
                    uid: i.Id,
                    state: FileState.Uploaded,
                    comment: i.FileDescription
                } as FileInfo;
            }) : null;
    }


    /**
     * 
     * @param dataItem 
     */
    private deleteAllAttachments(dataItem: any) {
        if (dataItem.RequestAttachmentDtos.length) {
            const f = dataItem.RequestAttachmentDtos.pop();
            this.handleS3Remove(f, dataItem)
                .then(x => this.deleteAllAttachments(dataItem));
        }
    }


    /**
     * 
     * @param file 
     * @param request 
     */
    private handleS3Remove(file, request: any = this.editDataItem): Promise<any> {
        const _path = `CTWR/${request.RequestNumber}/`;
        return this.s3Service.handleS3Remove(file, _path)
            .catch(e => null);
    }


    /**
     * Construct HttpParams using the gridState 
     */
    private getSearchParams(state: any = this.gridState, isExport = false): HttpParams {
        let _sort: any = state.sort[0];
        if (!_sort || !_sort.dir) _sort = { dir: 'desc', field: 'RequestNumber' };
        const _pageNumber = (state.skip / state.take) + 1;
        let httpParams: HttpParams = new HttpParams()
            .set('orderBy', _sort.field)
            .set('sortOrder', _sort.dir)
            .set('export', isExport.toString());

        if (!this.isAll) {
            httpParams = httpParams
                .set('pageSize', state.take.toString())
                .set('pageNumber', _pageNumber.toString())
        }

        return httpParams;
    }


    /**
     * Map and return filters from gridState
     */
    private getFilters(isExport = false) {
        const _filters: any = {};
        const gridFilter = this.gridState.filter;

        flatten(gridFilter).map(f => {
            const _field: string = f.field;
            let _values = f.value.toString().split(',').map(x => x.trim());
            if (_field === 'Subject' || _field === 'Detail') {
                _values = _values.join();
            }
            if (_filters[_field]) {
                _values.forEach(v => _filters[_field].push(v));
            } else if (_field.toLowerCase().includes('date') && f.operator) {
                const datePrefix = f.operator === 'gte' ? 'From' : 'To';
                _filters[`${datePrefix}${f.field}`] = f.value;
            } else {
                _filters[_field] = _values;
            }
        });
        return _filters;
    }


    handleExport = () => {
        const _path = 'ctwr/Request/Search';
        const d = new Date()
        const _fileType = '.xlsx';
        const _fileName = [
            'CTWR', 'Requests', d.getDate(), (d.getMonth() + 1), d.getFullYear()]
            .join('_') + _fileType;
        const _params = this.getSearchParams(this.gridState, true);

        this.service
            .export(this.getFilters(true), _path, _params)
            .then(response => {
                const nav = (window.navigator as any);
                const blob = new Blob([response], { type: "application/vnd.ms-excel" });
                if (typeof navigator !== "undefined" && nav.msSaveOrOpenBlob) {
                    nav.msSaveOrOpenBlob(blob, _fileName);
                }
                else {
                    const a = window.document.createElement("a");
                    a.href = window.URL.createObjectURL(blob);
                    a.download = _fileName;
                    document.body.appendChild(a)
                    a.click();
                }
            })
    }

    /****************************************************************************
     * TEMPLATE DEFINITIONS
     ***************************************************************************/

    customToolBarButtons = [{
        title: 'Export filtered requests to excel',
        text: 'Export to Excel',
        iconClass: 'fa fa-fw fa-file-excel-o',
        callback: this.handleExport,
    }];

    columns: any[] = [
        {
            field: "RequestNumber",
            title: "Request#",
            width: 160,
            template: {
                type: 'multiselect',
                allowCustom: true,
                service: this.requestNumberService,
            }
        },
        {
            field: 'ProcessName',
            title: 'Process',
            filterable: true,
            sortable: true,
            template: {
                type: 'multiselect',
                service: this.processNameService
            }
        },
        {
            field: "Subject",
            title: "Short Description",
        },
        {
            field: "Detail",
            title: "Full Description",
        },
        {
            field: "Originator",
            title: "Originator",
            filterable: true,
            sortable: true,
            template: {
                type: 'multiselect',
                service: this.originatorNameService
            }
        },
        {
            field: "RequestStatus",
            title: "Status",
            filterable: true,
            sortable: true,
            template: {
                type: 'multiselect',
                service: this.requestStatusService
            }
        },
        {
            field: "ModifiedBy",
            title: "Modified By",
            template: {
                type: 'multiselect',
                service: this.modifiedByService
            },
            visible: false,
        },
        {
            field: "ModifiedDate",
            title: "Modified Date",
            visible: false,
            template: {
                type: 'date'
            }
        },
        {
            field: "SubmissionDate",
            title: "Submission Date",
            visible: false,
            template: {
                type: 'date'
            }
        },

    ]
}
